import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { SideDrawerLayout } from "common/Components/SideDrawer";
import { GenericForm } from "common/Components/GenericForm";
import Toast from "common/Components/Toast";
import { FieldBlock } from "styles/formStyleComponents";
import CustomerAccessEditor from "./CustomerAccessEditor";
import { isInviteEmailFormatValid, useInviteEmailExists } from "../hooks/UseInviteEmailExists";
import {
  buildActiveCustomerSelectOptions,
  CUSTOMER_ACCESS_STATUS,
  DEFAULT_PLATFORM_ROLE_HINTS,
  enrichCustomerAccessRowFromActiveCustomer,
  PLATFORM_USER_ACCESS_UI,
  isNonePlatformRole,
  isPlatformAdminPlatformRole,
  normalizePlatformRoleValue,
  shouldShowPlatformAdminOptionalCustomerHint,
  validatePlatformCustomerAccess,
} from "../PlatformUserAccess.constants";
import { userAccessDrawerPaperSx } from "../PlatformUserAccess.styles";
import { resolveRoleHint } from "pages/Admin/Users/Users.constants";

const createEmptyCustomerAccess = () => ({
  customerId: "",
  roles: [],
  accessStatus: CUSTOMER_ACCESS_STATUS.INVITED,
});

const buildInviteEmailSection = () => [
  {
    wrapper: FieldBlock,
    fields: [
      {
        name: "email",
        type: "text",
        label: PLATFORM_USER_ACCESS_UI.inviteEmailLabel,
        placeholder: PLATFORM_USER_ACCESS_UI.inviteEmailPlaceholder,
        required: true,
        inputType: "email",
        hint: PLATFORM_USER_ACCESS_UI.inviteEmailHint,
        hintId: "invite-user-email-hint",
        inputProps: {
          id: "invite-user-email",
          "aria-describedby": "invite-user-email-hint",
          "data-testid": "invite-user-email",
        },
      },
    ],
  },
];

const buildInvitePlatformRoleSection = (platformRoleOptions, roleHint) => [
  {
    wrapper: FieldBlock,
    fields: [
      {
        name: "platformRole",
        type: "checkboxGroup",
        multiple: false,
        label: PLATFORM_USER_ACCESS_UI.invitePlatformRoleLabel,
        options: platformRoleOptions,
        labelId: "invite-user-role-label",
        testIdPrefix: "invite-platform-role",
        hint: roleHint,
      },
    ],
  },
];

const InviteUserModal = ({
  open,
  onClose,
  onSubmit,
  checkInviteEmail,
  platformRoleOptions,
  roleOptions,
  customers,
}) => {
  const [email, setEmail] = useState("");
  const [platformRole, setPlatformRole] = useState(
    PLATFORM_USER_ACCESS_UI.defaultPlatformRole
  );
  const [customerAccessRows, setCustomerAccessRows] = useState([
    createEmptyCustomerAccess(),
  ]);
  const [formErrors, setFormErrors] = useState({});
  const [customerAccessFormError, setCustomerAccessFormError] = useState("");
  const [rowErrors, setRowErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { checkEmailOnBlur } = useInviteEmailExists({ checkInviteEmail });

  const resolvedPlatformRole = normalizePlatformRoleValue(platformRole);
  const showPlatformAdminOptionalHint =
    shouldShowPlatformAdminOptionalCustomerHint(resolvedPlatformRole);

  const customerOptions = useMemo(
    () =>
      buildActiveCustomerSelectOptions({
        customers,
        selectedAccessRows: customerAccessRows,
      }),
    [customers, customerAccessRows]
  );

  const platformRoles = useMemo(
    () =>
      (Array.isArray(platformRoleOptions) ? platformRoleOptions : []).map(
        (option) => ({
          value: String(option.value ?? option.id ?? option.label ?? ""),
          label: String(option.label ?? option.value ?? ""),
          hint: resolveRoleHint(option, DEFAULT_PLATFORM_ROLE_HINTS),
        })
      ),
    [platformRoleOptions]
  );

  const resetState = () => {
    setEmail("");
    setPlatformRole(PLATFORM_USER_ACCESS_UI.defaultPlatformRole);
    setCustomerAccessRows([createEmptyCustomerAccess()]);
    setFormErrors({});
    setCustomerAccessFormError("");
    setRowErrors([]);
    setSubmitting(false);
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    resetState();
  }, [open]);

  const clearCustomerAccessValidation = () => {
    setCustomerAccessFormError("");
    setRowErrors([]);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleEmailChange = (_name, nextValue) => {
    setEmail(nextValue);
    setFormErrors((previous) => {
      if (!previous.email) {
        return previous;
      }
      const next = { ...previous };
      delete next.email;
      return next;
    });
  };

  const handleEmailBlur = async () => {
    const trimmed = String(email ?? "").trim();
    if (trimmed && !isInviteEmailFormatValid(trimmed)) {
      setFormErrors((previous) => ({
        ...previous,
        email: PLATFORM_USER_ACCESS_UI.invalidEmailError,
      }));
      return;
    }
    const { fieldError } = await checkEmailOnBlur(email);
    if (fieldError) {
      setFormErrors((previous) => ({ ...previous, email: fieldError }));
      return;
    }
    setFormErrors((previous) => {
      if (
        !previous.email ||
        previous.email === PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError ||
        previous.email === PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError ||
        previous.email === PLATFORM_USER_ACCESS_UI.invalidEmailError
      ) {
        const next = { ...previous };
        delete next.email;
        return next;
      }
      return previous;
    });
  };

  const normalizeRoleValues = (roles) =>
    (roles ?? []).map((role) =>
      typeof role === "string" ? role : role?.value || role?.label
    );

  const normalizeCustomerAccess = () =>
    customerAccessRows
      .filter((row) => row.customerId)
      .map((row) => {
        const enriched = enrichCustomerAccessRowFromActiveCustomer(
          row,
          customers
        );
        return {
          customerId: enriched.customerId,
          customerCode: enriched.customerCode,
          customerName: enriched.customerName,
          roles: normalizeRoleValues(enriched.roles).filter(Boolean),
          accessStatus:
            enriched.accessStatus ?? CUSTOMER_ACCESS_STATUS.INVITED,
        };
      });

  const handleSubmit = async () => {
    const trimmedEmail = String(email ?? "").trim();
    const nextFormErrors = {};

    if (!isInviteEmailFormatValid(trimmedEmail)) {
      nextFormErrors.email = PLATFORM_USER_ACCESS_UI.invalidEmailError;
    }
    if (
      formErrors.email === PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError ||
      formErrors.email === PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError
    ) {
      nextFormErrors.email = formErrors.email;
    }

    const accessValidation = validatePlatformCustomerAccess(
      customerAccessRows,
      resolvedPlatformRole
    );
    const hasAccessIssues =
      Boolean(accessValidation.formError) ||
      accessValidation.rowErrors.some(Boolean);

    if (nextFormErrors.email || hasAccessIssues) {
      setFormErrors(nextFormErrors);
      setCustomerAccessFormError(accessValidation.formError ?? "");
      setRowErrors(accessValidation.rowErrors);
      return;
    }

    if (typeof checkInviteEmail === "function") {
      const { exists, error } = await checkInviteEmail(trimmedEmail);
      if (error) {
        setFormErrors({
          email: PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError,
        });
        return;
      }
      if (exists) {
        setFormErrors({
          email: PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError,
        });
        return;
      }
    }

    setFormErrors({});
    setCustomerAccessFormError("");
    setRowErrors([]);
    setSubmitting(true);
    try {
      const success = await onSubmit({
        email: trimmedEmail,
        platformRole: resolvedPlatformRole,
        customerAccess: normalizeCustomerAccess(),
      });
      if (success) {
        resetState();
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const roleSummaryHint = isNonePlatformRole(platformRole)
    ? PLATFORM_USER_ACCESS_UI.inviteRoleSummaryNone
    : PLATFORM_USER_ACCESS_UI.inviteRoleSummaryPlatformAdmin;

  const handleFormChange = (name, value) => {
    if (name === "email") {
      handleEmailChange(name, value);
      return;
    }
    if (name === "platformRole") {
      setPlatformRole(value);
      clearCustomerAccessValidation();
    }
  };

  return (
    <SideDrawerLayout
      open={open}
      onClose={handleClose}
      title={PLATFORM_USER_ACCESS_UI.inviteDrawerTitle}
      titleId="invite-user-title"
      onSubmit={handleSubmit}
      submitLabel={PLATFORM_USER_ACCESS_UI.sendInviteLabel}
      cancelLabel={PLATFORM_USER_ACCESS_UI.cancelLabel}
      submitting={submitting}
      paperSx={userAccessDrawerPaperSx}
    >
      <Toast
        variant="banner"
        title={PLATFORM_USER_ACCESS_UI.inviteDescription}
        show
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          "& > div > div > div": {
            marginBottom: 1,
          },
        }}
      >
        <GenericForm
          sections={buildInviteEmailSection()}
          values={{
            email,
          }}
          onChange={handleFormChange}
          onFieldBlur={(name) => {
            if (name === "email") {
              handleEmailBlur();
            }
          }}
          errors={formErrors}
          showBorder={false}
        />
      </Box>
      <GenericForm
        sections={buildInvitePlatformRoleSection(platformRoles, roleSummaryHint)}
        values={{
          platformRole: normalizePlatformRoleValue(platformRole),
        }}
        onChange={handleFormChange}
        errors={formErrors}
        showBorder={false}
      />

      <CustomerAccessEditor
          userId={null}
          showDescription
          error={
            customerAccessFormError &&
            !isPlatformAdminPlatformRole(resolvedPlatformRole)
              ? customerAccessFormError
              : undefined
          }
          rows={customerAccessRows}
          rowErrors={rowErrors}
          customerOptions={customerOptions}
          activeCustomers={customers}
          roleOptions={roleOptions}
          onAddRow={() => {
            clearCustomerAccessValidation();
            setCustomerAccessRows((previous) => [
              ...previous,
              createEmptyCustomerAccess(),
            ]);
          }}
          onRemoveRow={(index) => {
            clearCustomerAccessValidation();
            setCustomerAccessRows((previous) =>
              previous.filter((_, currentIndex) => currentIndex !== index)
            );
          }}
          onRowChange={(index, name, value) => {
            clearCustomerAccessValidation();
            setCustomerAccessRows((previous) =>
              previous.map((row, currentIndex) => {
                if (currentIndex !== index) {
                  return row;
                }
                if (name === "customerId") {
                  const changed =
                    String(value ?? "") !== String(row.customerId ?? "");
                  const nextRow = changed
                    ? {
                        ...row,
                        customerId: value,
                        roles: [],
                        accessStatus: CUSTOMER_ACCESS_STATUS.INVITED,
                      }
                    : { ...row, customerId: value };
                  return enrichCustomerAccessRowFromActiveCustomer(
                    nextRow,
                    customers
                  );
                }
                return { ...row, [name]: value };
              })
            );
          }}
        />
    </SideDrawerLayout>
  );
};

InviteUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  checkInviteEmail: PropTypes.func,
  platformRoleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  customers: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default InviteUserModal;
